import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatTimestamp,
  INVOICE_CATEGORIES,
  listAllInvoices,
  type AdminInvoicePage,
  type InvoiceCategory,
  type InvoiceStatus
} from '../lib/invoices';

export function AdminInvoicesPage() {
  const [status, setStatus] = useState<InvoiceStatus>('UPLOADED');
  const [category, setCategory] = useState<'' | InvoiceCategory>('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [page, setPage] = useState<AdminInvoicePage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const query = useMemo(() => ({
    status,
    category: category || undefined,
    userId: userIdFilter || undefined,
    from: from ? new Date(from).toISOString() : undefined,
    to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
    limit: 50
  }), [status, category, userIdFilter, from, to]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listAllInvoices(query)
      .then((p) => { if (!cancelled) setPage(p); })
      .catch((e) => { if (!cancelled) setError(String((e as Error).message ?? e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [query]);

  return (
    <div>
      <h1>Admin — invoices</h1>
      <fieldset className="filters-bar section-card" style={filters}>
        <legend style={legend}>Filters</legend>
        <label style={fLabel}>
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value as InvoiceStatus)} style={input}>
            <option value="UPLOADED">UPLOADED</option>
            <option value="APPROVED">APPROVED</option>
          </select>
        </label>
        <label style={fLabel}>
          Category
          <select value={category} onChange={(e) => setCategory(e.target.value as '' | InvoiceCategory)} style={input}>
            <option value="">(any)</option>
            {INVOICE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label style={fLabel}>
          User OID
          <input
            type="text"
            placeholder="entra oid"
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.target.value)}
            style={input}
          />
        </label>
        <label style={fLabel}>
          From
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={input} />
        </label>
        <label style={fLabel}>
          To
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={input} />
        </label>
      </fieldset>

      {error && <p style={errStyle}>{error}</p>}
      {loading && <p style={muted}>Loading...</p>}

      {page && page.invoices.length === 0 && !loading && (
        <p style={muted}>No invoices match the current filters.</p>
      )}

      {page && page.invoices.length > 0 && (
        <div className="section-card" style={{ padding: 0 }}>
          <div className="table-scroll">
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>Created</th>
                  <th style={th}>User</th>
                  <th style={th}>Category</th>
                  <th style={th}>Status</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {page.invoices.map((inv) => (
                  <tr key={inv.invoiceId}>
                    <td style={td}>{formatTimestamp(inv.createdAt)}</td>
                    <td style={td}>{inv.userDisplayName || inv.userEmail}</td>
                    <td style={td}>
                      {inv.category}
                      {inv.category === 'Other' && inv.otherReason ? ` (${inv.otherReason})` : ''}
                    </td>
                    <td style={td}>
                      <StatusPill status={inv.status} locked={inv.isLocked} />
                    </td>
                    <td style={td}>
                      <Link to={`/admin/invoices/${inv.invoiceId}`} style={reviewLink}>Review</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status, locked }: { status: InvoiceStatus; locked: boolean }) {
  const approved = status === 'APPROVED';
  const bg = approved ? 'var(--sj-success-bg)' : 'var(--sj-warning-bg)';
  const color = approved ? 'var(--sj-success-fg)' : 'var(--sj-warning-fg)';
  const border = approved ? 'var(--sj-success-line)' : 'var(--sj-warning-line)';
  return (
    <span
      style={{
        background: bg,
        color,
        border: `1px solid ${border}`,
        padding: '2px 10px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase'
      }}
    >
      {status}{locked ? ' · locked' : ''}
    </span>
  );
}

const filters: React.CSSProperties = {
  display: 'flex',
  gap: 16,
  flexWrap: 'wrap',
  marginBottom: 20,
  border: 0,
  padding: 24
};

const legend: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: 'var(--sj-crimson)',
  padding: '0 8px',
  marginLeft: -8
};

const fLabel: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--sj-text-dark)'
};

const input: React.CSSProperties = {
  padding: '8px 10px',
  fontSize: 14,
  border: '1px solid var(--sj-line-strong)',
  borderRadius: 'var(--radius-xs)'
};

const table: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 14
};

const th: React.CSSProperties = {
  textAlign: 'left',
  borderBottom: '1px solid var(--sj-line-strong)',
  padding: '12px 16px',
  background: 'var(--sj-ink)',
  color: 'var(--sj-text-on-dark)',
  fontFamily: 'var(--font-body)',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase'
};

const td: React.CSSProperties = {
  borderBottom: '1px solid var(--sj-line)',
  padding: '12px 16px',
  color: 'var(--sj-text-body)'
};

const reviewLink: React.CSSProperties = {
  color: 'var(--sj-crimson)',
  fontWeight: 600,
  textTransform: 'uppercase',
  fontSize: 12,
  letterSpacing: '0.08em'
};

const muted: React.CSSProperties = {
  color: 'var(--sj-text-subtle)'
};

const errStyle: React.CSSProperties = {
  background: 'var(--sj-danger-bg)',
  border: '1px solid var(--sj-danger-line)',
  color: 'var(--sj-danger-fg)',
  padding: 12,
  borderRadius: 'var(--radius-xs)'
};
